import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronDown, Trophy } from 'lucide-react'
import { useState } from 'react'
import { MarkdownContent } from '@/components/markdown-content'
import type { IndividualMetricEvaluation } from '../types/pitch.types'
import { PERSONA_LABELS } from '../utils/scores'
import { ScoreBadge } from './score-badge'

interface WinnerJudgeCardProps {
  winnerPersona: string
  evaluations: IndividualMetricEvaluation[]
  score?: number
}

const METRIC_LABELS: Record<string, string> = {
  market: 'Market',
  problem_solution: 'Problem Solution',
  team: 'Team',
  traction: 'Traction',
  vision: 'Vision',
}

export function WinnerJudgeCard({ winnerPersona, evaluations, score }: WinnerJudgeCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  // Filter to only winner persona's evaluations
  const winnerEvals = evaluations.filter((e) => e.persona === winnerPersona)

  if (winnerEvals.length === 0) return null

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between p-4 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            <Trophy className="size-4 text-amber-400" />
            <span className="font-medium">{PERSONA_LABELS[winnerPersona] ?? winnerPersona}</span>
          </div>
          <div className="flex items-center gap-3">
            <ScoreBadge score={score} size="lg" />
            <ChevronDown
              className={cn(
                'size-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border p-4 space-y-6">
            {winnerEvals
              .sort((a, b) => a.metric.localeCompare(b.metric))
              .map((evaluation) => (
                <div
                  key={evaluation.id}
                  className="border-b border-border pb-6 last:border-0 last:pb-0"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {METRIC_LABELS[evaluation.metric] ?? evaluation.metric}
                    </span>
                    {evaluation.score !== null && <ScoreBadge score={evaluation.score} size="sm" />}
                  </div>
                  {evaluation.reasoning && (
                    <MarkdownContent>{evaluation.reasoning}</MarkdownContent>
                  )}
                </div>
              ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
